import { getConfig } from '@/config';
import { appendScript } from '@/utils';
import { triggerTypes } from '@/rules';

const serviceBase = {
	defaultAdapter: () => window.srv,
	serviceUrl: 'https://collect.mopinion.com/assets/surveys/2.0/js/survey.min.js',
};

const serviceConfig = {};

//keep global state of service urls currently being loaded -> prevent loading another duplicate while loading
const loadingServiceUrls = {};

/*services API requirements: 
- open (camelcase props -> trigger_method -> triggerMethod)
- isOpen
- clearForm
- addEventListener
- removeEventListener
*/
export default function createService(serviceName = 'mopinion') {
	const customConfig = getConfig(config => config[serviceName]) ?? {};
	let listeners = [];

	const config = {
		...serviceBase,
		...(serviceConfig[serviceName] && {
			...serviceConfig[serviceName],
		}),
		...customConfig,
	};
	console.log(`starting service ${serviceName}`);

	function getAdapter() {
		if (config.adapter) {
			return config.adapter;
		}
		return config.defaultAdapter();
	}

	function isLoaded() {
		return typeof getAdapter() !== 'undefined';
	}

	function isLoading() {
		return loadingServiceUrls[config.serviceUrl];
	}

	function setLoading(loading) {
		loadingServiceUrls[config.serviceUrl] = loading;
	}

	function start(callback = () => {}) {
		if (isLoaded()) {
			return callback();
		}

		if (isLoading()) {
			onLoaded(callback);
			return;
		}
		//modify global state to tracking currently loading urls
		setLoading(true);
		appendScript({
			src: `${config.serviceUrl}${config.version ? `?v=${config.version}` : ''}`,
			onLoad: () => {
				console.log(serviceName, 'loaded!');
				setLoading(false);
				callback();
				handleLoaded();
			},
		});
	}

	function onLoaded(callback) {
		if (isLoaded()) {
			callback();
		} else {
			listeners.push(callback);
		}
	}

	function handleLoaded() {
		listeners.forEach(callback => typeof callback === 'function' && callback());
		listeners = [];
	}

	function removeWhenClosed(key) {
		const interval = setInterval(() => {
			if (formIsOpen(key)) {
				return;
			}
			removeForm(key);
			clearInterval(interval);
		}, 100);
	}

	function handleTrigger({ trigger, ...props } = {}) {
		customConfig.refresh = getConfig(config => config.refresh);
		switch (trigger) {
			case triggerTypes.passive:
				return openForm({
					...customConfig,
					...props,
					cookie_expire: props.session,
					trigger_method: trigger,
					triggerMethod: trigger,
					showButton: true,
				});

			case triggerTypes.proactive:
			case triggerTypes.exit:
				return openForm({
					...customConfig,
					...props,
					cookie_expire: props.session,
					trigger_method: trigger,
					triggerMethod: trigger,
					formOpen: true,
				});

			default:
				console.log('trigger method not defined');
				return;
		}
	}

	function openForm(props = {}) {
		if (typeof config.openForm === 'function') {
			return config.openForm(props);
		}
		getAdapter().open(props);
	}

	function formIsOpen(key) {
		try {
			if (typeof config.formIsOpen === 'function') {
				return config.formIsOpen(key);
			}

			return getAdapter().isOpen(key);
		} catch (e) {
			return false;
		}
	}

	function removeForm(key) {
		try {
			if (typeof config.removeForm === 'function') {
				return config.removeForm(key);
			}
			return getAdapter().clearForm(key);
		} catch (e) {}
	}

	function addEventListener(event, fn) {
		try {
			getAdapter().addEventListener(event, fn);
		} catch (e) {}
	}

	function removeEventListener(event, fn) {
		try {
			getAdapter().removeEventListener(event, fn);
		} catch (e) {}
	}

	return {
		start,
		handleTrigger,
		openForm,
		removeWhenClosed,
		addEventListener,
		removeEventListener,
		onLoaded,
		isLoaded,
	};
}
